/**
 * Centralised date formatting so every surface speaks the same dialect.
 *
 * BW7 standardises on three forms:
 *   - "Sun 7 Jun"       — short user-facing date (CountdownBand subtitles,
 *                         vitals "as of"). Use formatShortDate().
 *   - "Thu 30/04"       — header day-of-week + day/month, used in Greeting.
 *                         Use formatHeaderDate().
 *   - "2026-06-08"      — ISO. Reserved for admin/debug surfaces and DB
 *                         payloads. Don't render this in the dashboard.
 *
 * All formatters accept either an ISO `yyyy-mm-dd` string or a Date and
 * return strings safe to render directly.
 */

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toDate(input: string | Date): Date | null {
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === "string" && input.length >= 10) {
    // Treat bare yyyy-mm-dd as local-midnight, not UTC, so timezone shifting
    // doesn't roll us a day back/forward.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** "Sun 7 Jun" style — concise, weekday-first, locale-independent. */
export function formatShortDate(input: string | Date): string {
  const d = toDate(input);
  if (!d) return typeof input === "string" ? input : "";
  return `${DAYS_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** "Thu 30/04" style — used on the Today header. */
export function formatHeaderDate(input: string | Date): string {
  const d = toDate(input);
  if (!d) return typeof input === "string" ? input : "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${DAYS_SHORT[d.getDay()]} ${dd}/${mm}`;
}

/** "2026-06-08" — ISO, for admin/debug only. */
export function formatIsoDate(input: string | Date): string {
  const d = toDate(input);
  if (!d) return typeof input === "string" ? input : "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
